import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, ArrowRight, CheckCircle2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : null;
const empty = { name: "", email: "", subject: "", message: "" };

const Field = ({ id, label, children }) => (
  <div className="group">
    <label htmlFor={id} className="block font-mono-x text-xs tracking-[0.18em] uppercase text-[var(--ink-muted)] mb-2 group-focus-within:text-[var(--emerald)] transition-colors">{label}</label>
    {children}
  </div>
);

const cls = "w-full bg-transparent border-b border-[#BFB5A5] focus:border-[var(--emerald)] outline-none py-3 text-lg transition-colors";

export const ContactForm = ({ journal = "index" }) => {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!API) { toast.error("Messaging is unavailable right now."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, { ...form, journal });
      setSent(true);
      setForm(empty);
      toast.success("Message sent. The editorial office will reply by email.");
    } catch {
      toast.error("Please check your details and try again.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-white border border-[var(--line)] p-10" data-testid="contact-success">
        <CheckCircle2 className="w-10 h-10 text-[var(--emerald)] mb-6" strokeWidth={1.25} />
        <p className="font-display text-3xl mb-3">Thank you — your message is with us.</p>
        <button onClick={() => setSent(false)} className="link-u text-[var(--emerald)]" data-testid="contact-send-another">Send another message</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="bg-white border border-[var(--line)] p-8 lg:p-12 space-y-8" data-testid="contact-form">
      <div className="grid sm:grid-cols-2 gap-8">
        <Field id="c-name" label="Name"><input id="c-name" required value={form.name} onChange={set("name")} className={cls} autoComplete="name" data-testid="contact-name-input" /></Field>
        <Field id="c-email" label="Email"><input id="c-email" type="email" required value={form.email} onChange={set("email")} className={cls} autoComplete="email" data-testid="contact-email-input" /></Field>
      </div>
      <Field id="c-subject" label="Subject"><input id="c-subject" value={form.subject} onChange={set("subject")} className={cls} data-testid="contact-subject-input" /></Field>
      <Field id="c-message" label="Message"><textarea id="c-message" required rows={5} value={form.message} onChange={set("message")} className={`${cls} resize-none`} data-testid="contact-message-input" /></Field>
      <div className="flex flex-col sm:flex-row gap-5 sm:items-center justify-between">
        <p className="text-sm text-[var(--ink-muted)] max-w-sm">Your details are used only to respond to this enquiry.</p>
        <button type="submit" disabled={busy} className="group inline-flex items-center justify-center gap-3 h-14 px-8 rounded-full bg-[var(--emerald)] text-[var(--paper)] hover:bg-[var(--emerald-2)] transition-colors disabled:opacity-60" data-testid="contact-submit-button">
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Send message <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" /></>}
        </button>
      </div>
    </form>
  );
};
